import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import {
  commandTicketsQuery,
  commandsSummaryQuery,
  sidebarTickets,
  ticketCommands,
  type CommandState,
  type CommandTicket,
  type SidebarTicket,
  type TicketCommand,
} from '../api/sidebar'
import { Spinner } from './Spinner'

/**
 * Bara laterala din dreapta - `_components/user_sidebar/sidebar.php` si
 * `resources/js/components/{user_sidebar,my_tickets,commands}.js`, cu acelasi
 * markup (`.user-sidebar .header .nav`, `.tickets-sidebar`, `.tabs-sidebar`,
 * `.commands-sidebar`) pe foaia portalului.
 *
 * Aici sunt filele MY TICKETS (`sidebar/easeedesk/userTickets`, cate 10 cu
 * "Load More") si COMMANDS (`sidebar/remote_control/tickets_*`, cu cele patru
 * insigne din `summary_counter` si comenzile desfacute pe tichetele gata ori
 * picate). Notifications, Chat, Collaborate si Memo NU sunt desenate: in v4
 * clopotelul, colaborarea si memo-ul sunt in antet (`NotificationsBell`,
 * `CollaborateBell`, `MemoBoard`), iar chatul operatorului vine cu ecranul
 * lui de suport.
 *
 * `openUserSidebar('commands', 'failed')` (bara de jos) deschide bara pe o fila
 * anume - `UserSidebar.navTo` + `Commands.triggerTab`.
 */

export const OPEN_EVENT = 'od:user-sidebar-open'

type Tab = 'my-tickets' | 'commands'

export function openUserSidebar(tab: Tab, state?: CommandState) {
  document.body.classList.add('user-sidebar-active')
  document.dispatchEvent(new CustomEvent(OPEN_EVENT, { detail: { tab, state } }))
}

const PER_PAGE = 10

function MyTickets() {
  const [rows, setRows] = useState<SidebarTicket[] | null>(null)
  const [page, setPage] = useState(1)
  const [more, setMore] = useState(false)
  const [loading, setLoading] = useState(false)

  const load = (next: number) => {
    setLoading(true)
    void sidebarTickets(next)
      .then((list) => {
        setRows((was) => (next === 1 || was === null ? list : [...was, ...list]))
        setMore(list.length >= PER_PAGE)
        setPage(next + 1)
      })
      .catch(() => setRows((was) => was ?? []))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    load(1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (rows === null) return <Spinner />

  return (
    <ul className="tickets-sidebar">
      {rows.map((t) => (
        <li className="ticket" key={t.ID}>
          <a className="media" target="_blank" href={`/app/easeedesk/ticket/${t.ID}`} rel="noreferrer">
            <span className="type badge" style={{ backgroundColor: t.type_color }}>
              {t.type_name}
            </span>
            <span className="id">#{t.ID}</span>
            <span className="subject">{t.subject}</span>
          </a>
          <div className="status" style={{ backgroundColor: t.status_color }}>
            {t.status_name}
          </div>
        </li>
      ))}
      {more ? (
        <li
          style={{ textAlign: 'center', marginTop: 20, cursor: 'pointer', fontWeight: 600, paddingBottom: 10 }}
          onClick={() => {
            if (!loading) load(page)
          }}
        >
          {loading ? <Spinner /> : <span className="color-danger font-12">Load More</span>}
        </li>
      ) : null}
    </ul>
  )
}

function Badge({ title, tone, count, icon }: { title: string; tone: string; count: number; icon: string }) {
  return (
    <div style={{ flex: 1, padding: '2px 10px 2px 0' }} className="font-12 text-center">
      <span style={{ display: 'block' }} title={title} className={`label label-${tone}`}>
        {count} <i className={icon}></i>
      </span>
    </div>
  )
}

/** Comenzile unui tichet, desfacute sub rand (`expandCommands`). */
function ExpandedCommands({ ticket }: { ticket: number }) {
  const [rows, setRows] = useState<TicketCommand[] | null>(null)
  useEffect(() => {
    void ticketCommands(ticket)
      .then(setRows)
      .catch(() => setRows([]))
  }, [ticket])
  if (rows === null) return <Spinner />
  return (
    <ul className="mt-1">
      {rows.map((c, i) => (
        <li className="mb-1" key={i}>
          <span className={`label label-${c.status === 'success' ? 'success' : 'danger'}`}>
            {c.status === 'success' ? 'Success' : 'Failed'}
          </span>{' '}
          {c.command_name}
        </li>
      ))}
    </ul>
  )
}

function CommandRow({ ticket, state }: { ticket: CommandTicket; state: CommandState }) {
  const [open, setOpen] = useState(false)
  const expandable = state === 'done' || state === 'failed'
  return (
    <li className="ticket" style={{ borderBottom: '1px solid #4f5769' }}>
      <div style={{ padding: '10px 10px 5px 10px', cursor: expandable ? 'pointer' : undefined }}>
        <div className="flex mb-1" style={{ flex: 1 }}>
          <div className="mr-1">
            <span className="type badge" style={{ padding: '3px 5px', fontSize: 9, backgroundColor: ticket.type_color }}>
              {ticket.type_name}
            </span>{' '}
            {ticket.subject}
          </div>
          <div style={{ marginLeft: 'auto' }}>
            <a target="_blank" href={`/app/easeedesk/ticket/${ticket.ID}`} rel="noreferrer">
              <label className="font-10 label label-default">{ticket.ID}</label>
            </a>
          </div>
        </div>
        <div
          className="flex"
          data-expand={expandable ? 'commands' : undefined}
          data-ticket={ticket.ID}
          onClick={() => {
            if (expandable) setOpen((was) => !was)
          }}
        >
          {state === 'sent' ? (
            <>
              <Badge title="Pending" tone="primary" count={ticket.sent} icon="fas fa-check-circle" />
              <div style={{ flex: 1, padding: '2px 0 2px 10px' }} className="font-12 text-right"></div>
            </>
          ) : state === 'running' ? (
            <>
              <Badge title="Success" tone="success" count={ticket.success} icon="fas fa-check-circle" />
              <Badge title="Failed" tone="danger" count={ticket.failed} icon="fas fa-times-circle" />
              <Badge title="Active" tone="warning" count={ticket.running} icon="fas fa-spinner fa-spin" />
            </>
          ) : state === 'failed' ? (
            <>
              <Badge title="Failed" tone="danger" count={ticket.failed} icon="fas fa-times-circle" />
              <Badge title="Success" tone="success" count={ticket.success} icon="fas fa-check-circle" />
            </>
          ) : (
            <>
              <Badge title="Success" tone="success" count={ticket.success} icon="fas fa-check-circle" />
              {ticket.failed > 0 ? (
                <Badge title="Failed" tone="danger" count={ticket.failed} icon="fas fa-times-circle" />
              ) : (
                <div style={{ flex: 1 }}></div>
              )}
            </>
          )}
        </div>
        <div className={`commands-container${open ? '' : ' hidden'}`}>{open ? <ExpandedCommands ticket={ticket.ID} /> : null}</div>
      </div>
    </li>
  )
}

const STATES: Array<{ key: CommandState; label: string; tone: string; summary: 'pending' | 'running' | 'failed' | 'done' }> = [
  { key: 'sent', label: 'Pending', tone: 'primary', summary: 'pending' },
  { key: 'running', label: 'Active', tone: 'warning', summary: 'running' },
  { key: 'failed', label: 'Failed', tone: 'danger', summary: 'failed' },
  { key: 'done', label: 'Done', tone: 'success', summary: 'done' },
]

function Commands({ initial }: { initial: CommandState }) {
  const [state, setState] = useState<CommandState>(initial)
  useEffect(() => setState(initial), [initial])
  const summary = useQuery(commandsSummaryQuery)
  const list = useQuery(commandTicketsQuery(state, 1))

  return (
    <>
      <ul className="flex tabs-sidebar">
        {STATES.map((s) => (
          <li key={s.key} data-action={s.key} className={state === s.key ? 'active' : ''} onClick={() => setState(s.key)}>
            <label
              id={`${s.key}-rc`}
              className={`badge badge-${s.tone}${s.key === 'failed' && (summary.data?.failed ?? 0) > 0 ? ' pulse' : ''}`}
            >
              {summary.data?.[s.summary] ?? 0}
            </label>{' '}
            {s.label}
          </li>
        ))}
      </ul>
      <ul className="commands-sidebar hover">
        {!list.data ? (
          <li>
            <Spinner />
          </li>
        ) : list.data.length === 0 ? (
          <li className="text-center font-14 mt-5">
            <i className="fa fa-code font-22 mb-2"></i>
            <p>No commands</p>
          </li>
        ) : (
          list.data.map((t) => <CommandRow key={t.ID} ticket={t} state={state} />)
        )}
      </ul>
    </>
  )
}

export function UserSidebar() {
  const [tab, setTab] = useState<Tab>('my-tickets')
  const [state, setState] = useState<CommandState>('sent')

  useEffect(() => {
    const onOpen = (ev: Event) => {
      const detail = (ev as CustomEvent<{ tab: Tab; state?: CommandState }>).detail
      if (!detail) return
      setTab(detail.tab)
      if (detail.state) setState(detail.state)
    }
    document.addEventListener(OPEN_EVENT, onOpen)
    return () => document.removeEventListener(OPEN_EVENT, onOpen)
  }, [])

  const nav = (key: Tab, title: string, icon: string) => (
    <li title={title} className={tab === key ? 'active' : ''}>
      <a
        href="#"
        data-toggle={key}
        onClick={(e) => {
          e.preventDefault()
          setTab(key)
        }}
      >
        <i className={`${icon} flaticon-20`}></i>
      </a>
    </li>
  )

  return (
    <div id="user-sidebar" className="user-sidebar">
      <div className="header">
        <ul className="nav">
          {nav('my-tickets', 'My Tickets', 'flaticon-interface-4')}
          {nav('commands', 'Commands', 'flaticon-internet')}
        </ul>
      </div>
      <div className="content" style={{ overflowY: 'auto' }}>
        {tab === 'my-tickets' ? <MyTickets /> : <Commands initial={state} />}
      </div>
    </div>
  )
}
